import { useState, useEffect, useRef } from "react";
import { Outlet, useLocation } from "react-router";
import type { User } from "firebase/auth";
import * as Sentry from "@sentry/react";
import { Sidebar } from "./Sidebar";
import { TopBar } from "./TopBar";

type AppShellProps = {
  user: User;
};

export function AppShell({ user }: AppShellProps) {
  const { pathname } = useLocation();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const mainRef = useRef<HTMLElement>(null);

  useEffect(() => {
    Sentry.setUser({ id: user.uid, email: user.email ?? undefined });
    return () => Sentry.setUser(null);
  }, [user.uid, user.email]);

  // Close mobile sidebar and scroll to top on navigation
  useEffect(() => {
    setMobileOpen(false);
    mainRef.current?.scrollTo(0, 0);
  }, [pathname]);

  // Prevent background scroll while the mobile drawer is open
  useEffect(() => {
    if (!mobileOpen) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [mobileOpen]);

  return (
    <div className="flex h-dvh overflow-hidden">
      {/* Mobile backdrop */}
      {mobileOpen && (
        <div
          className="fixed inset-0 z-40 touch-none overscroll-contain bg-black/50 lg:hidden"
          onClick={() => setMobileOpen(false)}
        />
      )}

      <Sidebar
        collapsed={collapsed}
        onToggle={() => setCollapsed((c) => !c)}
        mobileOpen={mobileOpen}
      />

      <div className="flex flex-1 flex-col overflow-hidden">
        <TopBar user={user} onMenuOpen={() => setMobileOpen(true)} />
        <main ref={mainRef} className="flex-1 overflow-y-auto p-4 md:p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
